import * as tslib_1 from "tslib";
import { LayerApi } from '../api/layer-api';
import { util } from '../classes/util';
import { ESRI } from '../esri-loader-util';
import { isClassBreaksRenderer, isDynamicDataLayerSource, isGeometryDataSource, isMapImageLayer, isSimpleRenderer, isSymbolWithOutline, isSymbolWithStyle, isUniqueValueRenderer } from '../types';
import { LegendService } from './legend.service';
import * as i0 from "@angular/core";
import * as i1 from "../api/layer-api";
import * as i2 from "./legend.service";
var ThemeService = /** @class */ (function () {
    function ThemeService(layerApi, legendService) {
        this.layerApi = layerApi;
        this.legendService = legendService;
        this.themeClassBreaks = {};
        this.themeColors = {};
        this.minMaxes = {
            unique: {
                min: 2,
                max: 100
            },
            range: {
                min: 3,
                max: 25
            }
        };
    }
    ThemeService.prototype.getLayerColumnsFromLayer = function (layer) {
        var retVal;
        if (layer && layer.mapcoreProps && layer.mapcoreProps.columns && layer.mapcoreProps.columns.length) {
            retVal = layer.mapcoreProps.columns
                .map(function (c) { return ({ name: c.name, alias: c.alias }); })
                .sort(function (a, b) {
                return a.alias.localeCompare(b.alias, 'en', { sensitivity: 'base', numeric: true });
            });
        }
        return retVal;
    };
    ThemeService.prototype.getLayerClassBreakData = function (layerId, fieldName, numSelectedClasses, forceUniqueValues, query, columnIsDate, classBreakLabels) {
        if (columnIsDate === void 0) { columnIsDate = false; }
        if (classBreakLabels === void 0) { classBreakLabels = '[]'; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var retVal, results;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.layerApi.getClassBreakData(layerId, [fieldName], numSelectedClasses, forceUniqueValues, query, columnIsDate, classBreakLabels)];
                    case 1:
                        results = _a.sent();
                        if (results && results.length) {
                            retVal = results[0].classBreakData;
                        }
                        return [2 /*return*/, retVal];
                }
            });
        });
    };
    ThemeService.prototype.getClassColors = function (count, currentColors, symbol, useSymbolScheme, useEsriScheme) {
        if (useSymbolScheme === void 0) { useSymbolScheme = false; }
        if (useEsriScheme === void 0) { useEsriScheme = false; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var colors, _a, Color, SymbologyColor, scheme, tenBase, fiveBase, defaultFillOpacity, esriColors_1, i, defaultColors, i;
            return tslib_1.__generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        colors = [];
                        return [4 /*yield*/, Promise.all([ESRI.Color, ESRI.SymbologyColor])];
                    case 1:
                        _a = tslib_1.__read.apply(void 0, [_b.sent(), 2]), Color = _a[0], SymbologyColor = _a[1];
                        scheme = SymbologyColor.getSchemes({ geometryType: 'polyline', basemap: this.mapView.map.basemap, theme: 'high-to-low' });
                        tenBase = 10;
                        fiveBase = 5;
                        defaultFillOpacity = 0.5;
                        if (!(symbol && (symbol.type === 'simple-fill' || useSymbolScheme) && (!isSymbolWithStyle(symbol) || (isSymbolWithStyle(symbol) && symbol.style !== 'none')) && this.getOpacity(symbol.color) !== 0)) return [3 /*break*/, 5];
                        if (!(currentColors && currentColors.length && currentColors.length === count)) return [3 /*break*/, 2];
                        colors = currentColors;
                        return [3 /*break*/, 4];
                    case 2: return [4 /*yield*/, this.getColorGradient(symbol.color, count)];
                    case 3:
                        colors = _b.sent();
                        _b.label = 4;
                    case 4: return [3 /*break*/, 6];
                    case 5:
                        if (useEsriScheme) {
                            esriColors_1 = [];
                            scheme.primaryScheme.colorsForClassBreaks.filter(function (x) { return x.numClasses === tenBase; })[0].colors.forEach(function (breakColor) {
                                esriColors_1.push(breakColor);
                            });
                            scheme.secondarySchemes[0].colorsForClassBreaks.filter(function (x) { return x.numClasses === tenBase; })[0].colors.forEach(function (breakColor) {
                                esriColors_1.push(breakColor);
                            });
                            scheme.secondarySchemes[1].colorsForClassBreaks.filter(function (x) { return x.numClasses === fiveBase; })[0].colors.forEach(function (breakColor) {
                                esriColors_1.push(breakColor);
                            });
                            if (symbol.type === 'simple-fill') {
                                esriColors_1.forEach(function (c) { return c.a = defaultFillOpacity; });
                            }
                            for (i = 0; i < this.minMaxes.unique.max; i++) {
                                colors.push(currentColors && currentColors.length > i ? currentColors[i] : esriColors_1[i]);
                            }
                        }
                        else {
                            defaultColors = this.legendService.getDefaultColors();
                            for (i = 0; i < this.minMaxes.unique.max; i++) {
                                if (defaultColors.length > i) {
                                    colors.push(Color.fromArray(defaultColors[i]));
                                }
                                else {
                                    colors.push(Color.fromArray(this.legendService.getRandomColorRGB()));
                                }
                            }
                            if (symbol.type === 'simple-fill') {
                                colors.forEach(function (c) { return c.a = defaultFillOpacity; });
                            }
                        }
                        _b.label = 6;
                    case 6: return [2 /*return*/, [colors, scheme]];
                }
            });
        });
    };
    ThemeService.prototype.getColorGradient = function (baseColor, length) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var Color, minOpacity, color, direction, avoidTooDarkOrLight, deltas, colors;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, ESRI.Color];
                    case 1:
                        Color = _a.sent();
                        minOpacity = 0.05;
                        color = Array.isArray(baseColor) ? baseColor : baseColor.toRgba();
                        direction = Math.max(color[0], color[1], color[2]) > 127 ? -1 : 1;
                        avoidTooDarkOrLight = 1.5;
                        deltas = color.map(function (h) {
                            var startPoint = direction === 1 ? 255 - h : h;
                            return Math.floor(startPoint / (length * avoidTooDarkOrLight) * direction);
                        });
                        colors = Array.from({ length: length }, function (_, i) {
                            return new Color([
                                color[0] + deltas[0] * i,
                                color[1] + deltas[1] * i,
                                color[2] + deltas[2] * i,
                                Math.max(color[3], minOpacity)
                            ]);
                        });
                        return [2 /*return*/, direction === 1 ? colors.reverse() : colors];
                }
            });
        });
    };
    ThemeService.prototype.getOpacity = function (color) {
        return Array.isArray(color) ? color[3] : color.a;
    };
    ThemeService.prototype.buildNumericRenderer = function (layer, field, classBreakInfos, symbols, sublayerColorSets) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var e_1, _a, renderers, ClassBreaksRenderer, i, renderer, classBreakInfos_1, classBreakInfos_1_1, classBreak, colors, customizedColors;
            return tslib_1.__generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        renderers = [];
                        return [4 /*yield*/, ESRI.ClassBreaksRenderer];
                    case 1:
                        ClassBreaksRenderer = _b.sent();
                        for (i = 0; i < layer.sublayers.length; i++) {
                            renderer = new ClassBreaksRenderer({ field: field });
                            try {
                                for (classBreakInfos_1 = tslib_1.__values(classBreakInfos), classBreakInfos_1_1 = classBreakInfos_1.next(); !classBreakInfos_1_1.done; classBreakInfos_1_1 = classBreakInfos_1.next()) {
                                    classBreak = classBreakInfos_1_1.value;
                                    renderer.addClassBreakInfo(classBreak);
                                    renderer.classBreakInfos[renderer.classBreakInfos.length - 1].value = classBreak.value;
                                }
                            }
                            catch (e_1_1) { e_1 = { error: e_1_1 }; }
                            finally {
                                try {
                                    if (classBreakInfos_1_1 && !classBreakInfos_1_1.done && (_a = classBreakInfos_1.return)) _a.call(classBreakInfos_1);
                                }
                                finally { if (e_1) throw e_1.error; }
                            }
                            colors = sublayerColorSets.length > i ? sublayerColorSets[i] : sublayerColorSets[0];
                            customizedColors = this.withThemeColors(layer, classBreakInfos, field, colors);
                            this.assignColorsToClasses(renderer, symbols[i], customizedColors);
                            layer.sublayers.getItemAt(i).renderer = renderer;
                            renderers.push(renderer);
                        }
                        return [2 /*return*/, renderers];
                }
            });
        });
    };
    ThemeService.prototype.buildUniqueValueRenderer = function (layer, field, classBreakInfos, symbols, sublayerColorSets) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var e_2, _a, renderers, UniqueValueRenderer, i, renderer, classBreakInfos_2, classBreakInfos_2_1, uniqueValue, colors, customizedColors;
            return tslib_1.__generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        renderers = [];
                        return [4 /*yield*/, ESRI.UniqueValueRenderer];
                    case 1:
                        UniqueValueRenderer = _b.sent();
                        for (i = 0; i < layer.sublayers.length; i++) {
                            renderer = new UniqueValueRenderer({ field: field });
                            try {
                                for (classBreakInfos_2 = tslib_1.__values(classBreakInfos), classBreakInfos_2_1 = classBreakInfos_2.next(); !classBreakInfos_2_1.done; classBreakInfos_2_1 = classBreakInfos_2.next()) {
                                    uniqueValue = classBreakInfos_2_1.value;
                                    renderer.addUniqueValueInfo({
                                        label: uniqueValue.label,
                                        value: uniqueValue.value
                                    });
                                }
                            }
                            catch (e_2_1) { e_2 = { error: e_2_1 }; }
                            finally {
                                try {
                                    if (classBreakInfos_2_1 && !classBreakInfos_2_1.done && (_a = classBreakInfos_2.return)) _a.call(classBreakInfos_2);
                                }
                                finally { if (e_2) throw e_2.error; }
                            }
                            if (layer.sublayers.getItemAt(i).renderer.visualVariables) {
                                renderer.visualVariables = layer.sublayers.getItemAt(i).renderer.visualVariables;
                            }
                            colors = sublayerColorSets.length > i ? sublayerColorSets[i] : sublayerColorSets[0];
                            customizedColors = this.withThemeColors(layer, classBreakInfos, field, colors);
                            this.assignColorsToClasses(renderer, symbols[i], customizedColors);
                            layer.sublayers.getItemAt(i).renderer = renderer;
                            renderers.push(renderer);
                        }
                        return [2 /*return*/, renderers];
                }
            });
        });
    };
    ThemeService.prototype.assignColorsToClasses = function (renderer, baseSymbol, colors) {
        var e_3, _a;
        var infos = [];
        if (isClassBreaksRenderer(renderer)) {
            infos = renderer.classBreakInfos;
        }
        else if (isUniqueValueRenderer(renderer)) {
            infos = renderer.uniqueValueInfos;
        }
        try {
            for (var _b = tslib_1.__values(infos.entries()), _c = _b.next(); !_c.done; _c = _b.next()) {
                var _d = tslib_1.__read(_c.value, 2), index = _d[0], info = _d[1];
                var symbol = util.deepClone(baseSymbol);
                if (isSymbolWithStyle(symbol) && symbol.style === 'none') {
                    symbol.style = 'solid';
                }
                symbol.color = colors[index];
                if (isSymbolWithOutline(symbol) && symbol.outline &&
                    symbol.outline.color.r === baseSymbol.color.r &&
                    symbol.outline.color.g === baseSymbol.color.g &&
                    symbol.outline.color.b === baseSymbol.color.b) {
                    symbol.outline.color = colors[index];
                }
                info.symbol = symbol;
            }
        }
        catch (e_3_1) { e_3 = { error: e_3_1 }; }
        finally {
            try {
                if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
            }
            finally { if (e_3) throw e_3.error; }
        }
    };
    ThemeService.prototype.updateLayerTheme = function (layer, performValidation, useSymbolScheme, useEsriScheme) {
        if (performValidation === void 0) { performValidation = true; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var currentSubLayer, recordCount, forceUniqueValues, numSelectedClasses, minClasses, maxClasses, errorMessage, isRangeThemed, selectedField_1, selectedColumn, isDate, savedClassBreakLabels, tempUniqueLabels, result, err_1;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        currentSubLayer = layer.sublayers.getItemAt(0);
                        return [4 /*yield*/, this.layerApi.getLayerRecordCount(layer.mapcoreProps.id || layer.mapcoreProps.parentId, layer.mapcoreProps.query)];
                    case 1:
                        recordCount = _a.sent();
                        if (!(recordCount && recordCount.length && recordCount[0].recordCount > 0
                            && (isUniqueValueRenderer(currentSubLayer.renderer) || isClassBreaksRenderer(currentSubLayer.renderer))
                            && isDynamicDataLayerSource(currentSubLayer.source) && isGeometryDataSource(currentSubLayer.source.dataSource))) return [3 /*break*/, 9];
                        forceUniqueValues = isUniqueValueRenderer(currentSubLayer.renderer);
                        numSelectedClasses = void 0;
                        minClasses = this.minMaxes.range.min;
                        maxClasses = this.minMaxes.range.max;
                        if (forceUniqueValues) {
                            minClasses = this.minMaxes.unique.min;
                            maxClasses = this.minMaxes.unique.max;
                        }
                        _a.label = 2;
                    case 2:
                        _a.trys.push([2, 7, , 8]);
                        errorMessage = '';
                        isRangeThemed = void 0;
                        selectedField_1 = currentSubLayer.renderer.field;
                        selectedColumn = layer.mapcoreProps.columns.find(function (x) { return x.name.toLowerCase() === selectedField_1.toLowerCase(); });
                        isDate = selectedColumn.databaseDataType === 'DATE' || selectedColumn.name.includes('DATE');
                        savedClassBreakLabels = this.getClassBreakLabels(layer);
                        if (isUniqueValueRenderer(currentSubLayer.renderer)) {
                            numSelectedClasses = currentSubLayer.renderer.uniqueValueInfos.length;
                            isRangeThemed = false;
                            // Sets custom labels found within layer in layer_symbol, uses only if the labels are different than their corresponding value
                            if (!savedClassBreakLabels && !layer.mapcoreProps.classBreakLabels) {
                                tempUniqueLabels = currentSubLayer.renderer.uniqueValueInfos
                                    .filter(function (info) { return info.label !== info.value; })
                                    .map(function (info) { return [info.value, info.label]; });
                                if (tempUniqueLabels.length) {
                                    layer.mapcoreProps.classBreakLabels = JSON.stringify(tempUniqueLabels);
                                }
                            }
                        }
                        else if (isClassBreaksRenderer(currentSubLayer.renderer)) {
                            numSelectedClasses = currentSubLayer.renderer.classBreakInfos.length;
                            isRangeThemed = true;
                        }
                        return [4 /*yield*/, this.getLayerClassBreakData(layer.mapcoreProps.parentId, selectedField_1, numSelectedClasses, forceUniqueValues, JSON.parse(currentSubLayer.source.dataSource.query), isDate, savedClassBreakLabels || layer.mapcoreProps.classBreakLabels)];
                    case 3:
                        result = _a.sent();
                        if (performValidation) {
                            errorMessage = this.validateMaxClasses(result, minClasses, maxClasses, forceUniqueValues);
                        }
                        if (!(errorMessage === '')) return [3 /*break*/, 5];
                        return [4 /*yield*/, this.applyLayerTheme(layer, result, selectedField_1, forceUniqueValues, isRangeThemed, useEsriScheme, useSymbolScheme, isDate)];
                    case 4:
                        _a.sent();
                        return [3 /*break*/, 6];
                    case 5: throw new Error(errorMessage);
                    case 6: return [3 /*break*/, 8];
                    case 7:
                        err_1 = _a.sent();
                        throw new Error('There was an error loading data, please try again or contact an administrator.');
                    case 8: return [3 /*break*/, 10];
                    case 9: throw new Error('No records for the current layer. Theme can\'t be applied.');
                    case 10: return [2 /*return*/];
                }
            });
        });
    };
    ThemeService.prototype.applyLayerTheme = function (layer, result, selectedField, forceUniqueValues, isRangeThemed, useEsriScheme, useSymbolScheme, isDate) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var symbols, currentColors, _a, colors;
            return tslib_1.__generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4 /*yield*/, this.getSimpleLayerSymbols(layer)];
                    case 1:
                        symbols = _b.sent();
                        currentColors = this.getSublayerColors(layer.sublayers.getItemAt(0));
                        return [4 /*yield*/, this.getClassColors(result.ClassBreakInfos.length, currentColors, symbols[0], this.shouldUseEsriScheme(useEsriScheme), this.shouldUseSymbolScheme(useSymbolScheme))];
                    case 2:
                        _a = tslib_1.__read.apply(void 0, [_b.sent(), 1]), colors = _a[0];
                        if (!((result.IsNumericData || isDate) && !forceUniqueValues && isRangeThemed)) return [3 /*break*/, 4];
                        return [4 /*yield*/, this.buildNumericRenderer(layer, selectedField, result.ClassBreakInfos, symbols, [colors])];
                    case 3:
                        _b.sent();
                        return [3 /*break*/, 6];
                    case 4: return [4 /*yield*/, this.buildUniqueValueRenderer(layer, selectedField, result.ClassBreakInfos, symbols, [colors])];
                    case 5:
                        _b.sent();
                        _b.label = 6;
                    case 6:
                        if (!layer.mapcoreProps.classBreakLabels) {
                            layer.mapcoreProps.classBreakLabels = this.generateClassBreakLabels(layer);
                        }
                        layer.refresh();
                        window.dispatchEvent(new CustomEvent('symbolChange', { detail: layer.id }));
                        return [2 /*return*/];
                }
            });
        });
    };
    ThemeService.prototype.validateMaxClasses = function (result, minClasses, maxClasses, forceUniqueValues) {
        var errorMessage = '';
        if (result.ClassBreakInfos.length < minClasses) {
            errorMessage = "This field has fewer than " + minClasses + " class breaks. Please try changing the layer filter or selecting a different field.";
        }
        else if (result.ClassBreakInfos.length > maxClasses) {
            errorMessage = "This field has more than " + maxClasses + " class breaks. Please try filtering the layer or selecting a different field.";
        }
        else if (result.ClassBreakInfos.length < minClasses && result.IsNumericData && !forceUniqueValues) {
            errorMessage = 'Not enough records for theme.';
        }
        return errorMessage;
    };
    ThemeService.prototype.shouldUseEsriScheme = function (useEsriScheme) {
        var useEsri = useEsriScheme;
        if (useEsri === undefined || useEsri === null) {
            useEsri = true; // defaults to true if no theme editor is found
            var themeEditors = document.getElementsByTagName('mc-theme-editor');
            if (themeEditors.length) {
                var useEsriAttrib = themeEditors[0].attributes['use-esri-scheme'];
                if (useEsriAttrib) {
                    useEsri = useEsriAttrib.value === 'true';
                }
            }
        }
        return useEsri;
    };
    ThemeService.prototype.shouldUseSymbolScheme = function (useSymbolScheme) {
        var useSymbol = useSymbolScheme;
        if (useSymbol === undefined || useSymbol === null) {
            useSymbol = false; // defaults to false if no theme editor is found
            var themeEditors = document.getElementsByTagName('mc-theme-editor');
            if (themeEditors.length) {
                var useSymbolAttrib = themeEditors[0].attributes['use-symbol-scheme'];
                if (useSymbolAttrib) {
                    useSymbol = useSymbolAttrib.value === 'true';
                }
            }
        }
        return useSymbol;
    };
    ThemeService.prototype.getSimpleLayerSymbols = function (layer) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var symbols, i, _a, _b;
            return tslib_1.__generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        symbols = [];
                        i = 0;
                        _c.label = 1;
                    case 1:
                        if (!(i < layer.sublayers.length)) return [3 /*break*/, 4];
                        _b = (_a = symbols).push;
                        return [4 /*yield*/, this.getSimpleSublayerSymbol(layer, i)];
                    case 2:
                        _b.apply(_a, [_c.sent()]);
                        _c.label = 3;
                    case 3:
                        i++;
                        return [3 /*break*/, 1];
                    case 4: return [2 /*return*/, symbols];
                }
            });
        });
    };
    ThemeService.prototype.getSimpleSublayerSymbol = function (layer, index) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var symbol, r;
            return tslib_1.__generator(this, function (_a) {
                r = layer.sublayers.getItemAt(index).renderer;
                if (isSimpleRenderer(r)) {
                    symbol = r.symbol;
                }
                else if (isClassBreaksRenderer(r)) {
                    symbol = r.classBreakInfos[0].symbol;
                }
                else if (isUniqueValueRenderer(r)) {
                    symbol = r.uniqueValueInfos[0].symbol;
                }
                else if (layer.mapcoreProps.simpleRenderers) {
                    symbol = layer.mapcoreProps.simpleRenderers[index].symbol;
                }
                return [2 /*return*/, symbol];
            });
        });
    };
    ThemeService.prototype.getSublayerColors = function (sublayer) {
        var colors;
        var r = sublayer.renderer;
        if (isSimpleRenderer(r)) {
            colors = [r.symbol.color];
        }
        else if (isClassBreaksRenderer(r)) {
            colors = r.classBreakInfos.map(function (i) { return i.symbol.color; });
        }
        else if (isUniqueValueRenderer(r)) {
            colors = r.uniqueValueInfos.map(function (i) { return i.symbol.color; });
        }
        return colors;
    };
    ThemeService.prototype.getNewRendererForSublayer = function (sublayer) {
        if (isDynamicDataLayerSource(sublayer.source) && isGeometryDataSource(sublayer.source.dataSource)) {
            var geometryType = sublayer.source.dataSource.geometryType.replace('polyline', 'line');
            return this.getNewRendererDefinitonForGeometryType(geometryType);
        }
    };
    ThemeService.prototype.getNewRendererDefinitonForGeometryType = function (geometryType, keepColor) {
        var rendererDefinitonToReturn = {
            type: 'simple',
            symbol: this.getSymbolDefinitionForLayerGeometryType(geometryType, keepColor)
        };
        return rendererDefinitonToReturn;
    };
    // Returns the proper renderer definition for the specified geometry type
    ThemeService.prototype.getRenderDefinitionForGeometryType = function (geometryType, keepColor, renderers) {
        if (renderers === void 0) { renderers = null; }
        if (renderers === null || renderers.length === 0) {
            // Generate new renderer
            return this.getNewRendererDefinitonForGeometryType(geometryType, keepColor);
        }
        else {
            // These are symbols, not renderers, so handle here.
            if (renderers.filter(function (x) { return x.color; }).length > 0) {
                renderers = renderers.map(function (x) { return ({ type: 'simple', symbol: x }); });
            }
            var renderer = renderers[0];
            if (renderers.length > 1) {
                renderer = this.legendService.getCorrectRendererGivenGeometryType(geometryType, renderers);
            }
            return this.legendService.getModifiedRenderer(renderer);
        }
    };
    ThemeService.prototype.getSymbolDefinitionForLayerGeometryType = function (geometryType, keepColor) {
        if (keepColor === void 0) { keepColor = false; }
        var color = this.legendService.getNewColor(this.mapView.map, keepColor);
        return this.getSymbolDefinitionFromGeometry(geometryType, color);
    };
    ThemeService.prototype.getSymbolDefinitionFromGeometry = function (geometryType, color, outlineColor) {
        if (outlineColor === void 0) { outlineColor = null; }
        return this.legendService.getSymbolDefinitionFromGeometry(geometryType, color, outlineColor);
    };
    ThemeService.prototype.getColorsForField = function (layer, field) {
        if (!this.themeColors[layer.id]) {
            this.themeColors[layer.id] = {};
        }
        field = field.toLowerCase();
        if (!this.themeColors[layer.id][field]) {
            this.themeColors[layer.id][field] = {};
        }
        return this.themeColors[layer.id][field];
    };
    ThemeService.prototype.saveColor = function (layer, field, value, color) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var Color, fieldColors;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, ESRI.Color];
                    case 1:
                        Color = _a.sent();
                        fieldColors = this.getColorsForField(layer, field);
                        fieldColors[value] = new Color(color);
                        return [2 /*return*/];
                }
            });
        });
    };
    ThemeService.prototype.forgetColor = function (layer, field, value) {
        var fieldColors = this.getColorsForField(layer, field);
        // tslint:disable-next-line:no-dynamic-delete
        delete fieldColors[value];
    };
    ThemeService.prototype.saveTheme = function (layer, renderer) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var e_4, _a, Color, infos, infos_1, infos_1_1, info;
            return tslib_1.__generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4 /*yield*/, ESRI.Color];
                    case 1:
                        Color = _b.sent();
                        infos = isUniqueValueRenderer(renderer)
                            ? renderer.uniqueValueInfos
                            : renderer.classBreakInfos;
                        if (isClassBreaksRenderer(renderer)) {
                            if (!this.themeClassBreaks[layer.id]) {
                                this.themeClassBreaks[layer.id] = {};
                            }
                            this.themeClassBreaks[layer.id][renderer.field.toLowerCase()] = this.generateClassBreakLabels(layer);
                        }
                        try {
                            for (infos_1 = tslib_1.__values(infos), infos_1_1 = infos_1.next(); !infos_1_1.done; infos_1_1 = infos_1.next()) {
                                info = infos_1_1.value;
                                this.saveColor(layer, renderer.field, info.value || info.label, new Color(info.symbol.color));
                            }
                        }
                        catch (e_4_1) { e_4 = { error: e_4_1 }; }
                        finally {
                            try {
                                if (infos_1_1 && !infos_1_1.done && (_a = infos_1.return)) _a.call(infos_1);
                            }
                            finally { if (e_4) throw e_4.error; }
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    ThemeService.prototype.getClassBreakLabels = function (layer) {
        var layerClassBreaks = this.themeClassBreaks[layer.id];
        if (layerClassBreaks && isMapImageLayer(layer)) {
            var field = layer.allSublayers.toArray()[0].renderer.field.toLowerCase();
            return layerClassBreaks[field];
        }
        else {
            return null;
        }
    };
    ThemeService.prototype.generateClassBreakLabels = function (layer) {
        var renderer = layer.allSublayers.toArray()[0].renderer;
        if (!isClassBreaksRenderer(renderer)) {
            return;
        }
        var classBreakLabels = renderer.classBreakInfos.map(function (cb) { return ["" + cb.minValue, "" + cb.label]; });
        var maximumClassBreak = renderer.classBreakInfos.slice(-1)[0];
        classBreakLabels.push([maximumClassBreak.maxValue.toString(), null]);
        return JSON.stringify(classBreakLabels);
    };
    ThemeService.prototype.saveClassBreakLabels = function (layer) {
        if (!this.themeClassBreaks[layer.id]) {
            this.themeClassBreaks[layer.id] = {};
        }
        var field = layer.allSublayers.toArray()[0].renderer.field.toLowerCase();
        this.themeClassBreaks[layer.id][field] = layer.mapcoreProps.classBreakLabels;
    };
    ThemeService.prototype.forgetTheme = function (layer) {
        // tslint:disable:no-dynamic-delete
        delete this.themeColors[layer.id];
        delete this.themeClassBreaks[layer.id];
        // tslint:enable:no-dynamic-delete
    };
    ThemeService.prototype.withThemeColors = function (layer, classBreaks, field, colors) {
        var e_5, _a;
        var fieldColors = this.getColorsForField(layer, field);
        colors = colors.slice();
        if (fieldColors) {
            try {
                for (var _b = tslib_1.__values(classBreaks.entries()), _c = _b.next(); !_c.done; _c = _b.next()) {
                    var _d = tslib_1.__read(_c.value, 2), index = _d[0], classBreak = _d[1];
                    if (classBreak.value in fieldColors) {
                        colors[index] = fieldColors[classBreak.value];
                    }
                }
            }
            catch (e_5_1) { e_5 = { error: e_5_1 }; }
            finally {
                try {
                    if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
                }
                finally { if (e_5) throw e_5.error; }
            }
        }
        return colors;
    };
    ThemeService.ngInjectableDef = i0.defineInjectable({ factory: function ThemeService_Factory() { return new ThemeService(i0.inject(i1.LayerApi), i0.inject(i2.LegendService)); }, token: ThemeService, providedIn: "root" });
    return ThemeService;
}());
export { ThemeService };
